(function () {
  const ROOT_SELECTOR = 'custom-header-trio-slider';
  const TRACK_SELECTOR = '[data-trio-track]';
  const VIEWPORT_SELECTOR = '.custom-header-trio-slider__viewport';
  const WRAPPER_SELECTOR = '.f-hero__wrapper';
  const DOTS_SELECTOR = '[data-trio-dots]';
  const DOT_SELECTOR = '[data-trio-dot]';
  const INTERACTIVE_SELECTOR = 'button, a, input, textarea, select, label';
  const DRAG_THRESHOLD_RATIO = 0.12;

  class CustomHeaderTrioSlider extends HTMLElement {
    connectedCallback() {
      if (this._initialized) return;
      this._initialized = true;

      this.track = this.querySelector(TRACK_SELECTOR);
      this.viewport = this.querySelector(VIEWPORT_SELECTOR);
      this.wrapper = this.closest(WRAPPER_SELECTOR);
      this.dragSurface = this.wrapper || this.viewport;
      this.dotsWrap = this.querySelector(DOTS_SELECTOR);
      this.dots = this.dotsWrap ? [...this.dotsWrap.querySelectorAll(DOT_SELECTOR)] : [];
      this.slides = this.track ? [...this.track.children] : [];
      this.index = 0;
      this.timer = null;
      this.autoplay = this.dataset.autoplay === 'true';
      this.speed = Number(this.dataset.speed || 5) * 1000;
      this.dragging = false;
      this.dragStartX = 0;
      this.dragStartY = 0;
      this.dragStartIndex = 0;
      this.activePointerId = null;
      this.isHorizontalDrag = false;

      if (!this.track || !this.viewport || !this.dragSurface || this.slides.length === 0) return;

      this.onPointerDown = this.onPointerDown.bind(this);
      this.onPointerMove = this.onPointerMove.bind(this);
      this.onPointerUp = this.onPointerUp.bind(this);
      this.onPointerCancel = this.onPointerUp.bind(this);

      this.dots.forEach((dot) => {
        dot.addEventListener('click', () => {
          const nextIndex = Number(dot.dataset.trioDot);
          if (!Number.isNaN(nextIndex)) this.goTo(nextIndex, true);
        });
      });

      this.dragSurface.addEventListener('pointerdown', this.onPointerDown);
      this.dragSurface.addEventListener('dragstart', (event) => event.preventDefault());

      this.addEventListener('mouseenter', () => this.pause());
      this.addEventListener('mouseleave', () => {
        if (!this.dragging) this.play();
      });
      this.addEventListener('focusin', () => this.pause());
      this.addEventListener('focusout', (event) => {
        if (!this.contains(event.relatedTarget) && !this.dragging) this.play();
      });

      this.goTo(0, false);
      this.play();
    }

    disconnectedCallback() {
      this.endDrag();
      this.pause();

      if (this.dragSurface) {
        this.dragSurface.removeEventListener('pointerdown', this.onPointerDown);
      }

      document.removeEventListener('pointermove', this.onPointerMove);
      document.removeEventListener('pointerup', this.onPointerUp);
      document.removeEventListener('pointercancel', this.onPointerCancel);
    }

    isInteractiveTarget(target) {
      return Boolean(target && target.closest && target.closest(INTERACTIVE_SELECTOR));
    }

    setDraggingState(isDragging) {
      this.viewport.classList.toggle('is-dragging', isDragging);
      if (this.wrapper) {
        this.wrapper.classList.toggle('is-trio-dragging', isDragging);
      }
    }

    onPointerDown(event) {
      if (this.slides.length < 2) return;
      if (event.pointerType === 'mouse' && event.button !== 0) return;
      if (this.isInteractiveTarget(event.target)) return;

      this.dragging = true;
      this.isHorizontalDrag = false;
      this.dragStartX = event.clientX;
      this.dragStartY = event.clientY;
      this.dragStartIndex = this.index;
      this.activePointerId = event.pointerId;
      this.pause();
      this.setDraggingState(true);
      this.track.style.transition = 'none';

      document.addEventListener('pointermove', this.onPointerMove, { passive: false });
      document.addEventListener('pointerup', this.onPointerUp);
      document.addEventListener('pointercancel', this.onPointerCancel);

      if (this.dragSurface.setPointerCapture) {
        this.dragSurface.setPointerCapture(event.pointerId);
      }
    }

    onPointerMove(event) {
      if (!this.dragging || event.pointerId !== this.activePointerId) return;

      const deltaX = event.clientX - this.dragStartX;
      const deltaY = event.clientY - this.dragStartY;

      if (!this.isHorizontalDrag) {
        if (Math.abs(deltaX) < 8 && Math.abs(deltaY) < 8) return;
        this.isHorizontalDrag = Math.abs(deltaX) >= Math.abs(deltaY);
        if (!this.isHorizontalDrag) {
          this.endDrag();
          this.goTo(this.index, false);
          return;
        }
      }

      event.preventDefault();

      const slideWidth = this.viewport.offsetWidth || 1;
      const deltaPercent = (deltaX / slideWidth) * 100;
      const translateX = -(this.dragStartIndex * 100) + deltaPercent;

      this.track.style.transform = `translate3d(${translateX}%, 0, 0)`;
    }

    onPointerUp(event) {
      if (!this.dragging || event.pointerId !== this.activePointerId) return;

      const wasHorizontalDrag = this.isHorizontalDrag;
      const slideWidth = this.viewport.offsetWidth || 1;
      const deltaX = event.clientX - this.dragStartX;
      const threshold = slideWidth * DRAG_THRESHOLD_RATIO;
      let nextIndex = this.dragStartIndex;

      if (wasHorizontalDrag) {
        if (deltaX <= -threshold) {
          nextIndex = Math.min(this.slides.length - 1, this.dragStartIndex + 1);
        } else if (deltaX >= threshold) {
          nextIndex = Math.max(0, this.dragStartIndex - 1);
        }
      }

      this.endDrag();

      if (wasHorizontalDrag) {
        this.goTo(nextIndex, true);
      } else {
        this.play();
      }
    }

    endDrag() {
      if (!this.dragging) return;

      const pointerId = this.activePointerId;
      this.dragging = false;
      this.isHorizontalDrag = false;
      this.activePointerId = null;
      this.setDraggingState(false);
      this.track.style.transition = '';

      document.removeEventListener('pointermove', this.onPointerMove);
      document.removeEventListener('pointerup', this.onPointerUp);
      document.removeEventListener('pointercancel', this.onPointerCancel);

      if (this.dragSurface.releasePointerCapture && pointerId != null) {
        try {
          this.dragSurface.releasePointerCapture(pointerId);
        } catch (_error) {
          // Pointer may already be released.
        }
      }
    }

    pause() {
      if (this.timer) {
        clearInterval(this.timer);
        this.timer = null;
      }
    }

    play() {
      this.pause();
      if (!this.autoplay || this.slides.length < 2 || this.dragging) return;

      this.timer = setInterval(() => {
        const next = (this.index + 1) % this.slides.length;
        this.goTo(next, false);
      }, this.speed);
    }

    goTo(index, userTriggered) {
      if (index < 0 || index >= this.slides.length) return;

      this.index = index;

      if (!this.dragging) {
        this.track.style.transition = '';
      }

      this.track.style.transform = `translate3d(-${index * 100}%, 0, 0)`;

      this.dots.forEach((dot, dotIndex) => {
        if (dotIndex === index) {
          dot.setAttribute('aria-current', 'true');
        } else {
          dot.removeAttribute('aria-current');
        }
      });

      if (userTriggered) this.play();
    }
  }

  if (!customElements.get(ROOT_SELECTOR)) {
    customElements.define(ROOT_SELECTOR, CustomHeaderTrioSlider);
  }
})();
